// src/components/admin/MonitoringDashboard.tsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "../../i18n";
import { fetchData } from "../../api/apiHelpers";
import useChartTheme from "../dashboards/charts/useChartTheme";
import {
  glass,
  glassAxis,
  glassChartTooltip,
} from "../../theme/glassTokens";

interface ConcurrencyData {
  channels: {
    interactive: { active: number; max_theoretical: number; reserved: number };
    bulk: { active: number; max_theoretical: number };
    voice: { active: number; max: number };
  };
  total_text_slots: number;
  text_slots_in_use: number;
}

interface TokenRateMinute {
  minute: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
}

interface TokenRateData {
  minutes: TokenRateMinute[];
  current_rate_per_min: number;
  total_last_hour: number;
}

interface CostSummaryData {
  period: string;
  month_start: string;
  tokens: { prompt: number; completion: number; total: number };
  cost_usd: { input: number; output: number; total: number };
  by_channel: Record<string, { tokens: number; calls: number }>;
}

interface UsageHistoryEntry {
  date: string;
  interactive: number;
  bulk: number;
  voice: number;
  cum_interactive: number;
  cum_bulk: number;
  cum_voice: number;
  cum_total: number;
}

interface UsageHistoryData {
  period_days: number;
  daily: UsageHistoryEntry[];
}

const POLL_INTERVAL_MS = 10_000;

const SlotGauge: React.FC<{
  label: string;
  active: number;
  max: number;
  color: string;
}> = ({ label, active, max, color }) => {
  const { t } = useTranslation();
  const textSecondary = glass.textSecondary;
  const pct = max > 0 ? (active / max) * 100 : 0;
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: glass.panel,
        border: `1px solid ${glass.panelBorder}`,
        backdropFilter: "blur(8px)",
        transition: "border-color 0.2s ease",
        "&:hover": { borderColor: glass.headerDivider },
        height: "100%",
      }}
    >
      <Typography variant="caption" sx={{ color: textSecondary }} fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5, mb: 1 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>
          {active}
        </Typography>
        <Typography variant="body2" sx={{ color: textSecondary }}>/ {max}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: alpha(color, 0.1),
          "& .MuiLinearProgress-bar": {
            borderRadius: 4,
            bgcolor: color,
          },
        }}
      />
      <Typography variant="caption" sx={{ color: textSecondary, mt: 0.5, display: "block" }}>
        {pct.toFixed(0)}% {t("monitoring.utilization", "utilization")}
      </Typography>
    </Box>
  );
};

const MonitoringDashboard: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const chartTheme = useChartTheme();
  const [concurrency, setConcurrency] = useState<ConcurrencyData | null>(null);
  const [tokenRate, setTokenRate] = useState<TokenRateData | null>(null);
  const [cost, setCost] = useState<CostSummaryData | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const refresh = useCallback(async () => {
    try {
      const [c, tr, cs, uh] = await Promise.all([
        fetchData<ConcurrencyData>("/billing/admin/monitor/concurrency/").catch(() => null),
        fetchData<TokenRateData>("/billing/admin/monitor/token-rate/").catch(() => null),
        fetchData<CostSummaryData>("/billing/admin/monitor/cost-summary/").catch(() => null),
        fetchData<UsageHistoryData>("/billing/admin/monitor/usage-history/?days=30").catch(() => null),
      ]);
      if (c) setConcurrency(c);
      if (tr) setTokenRate(tr);
      if (cs) setCost(cs);
      if (uh) setUsageHistory(uh);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const minutes = tokenRate?.minutes || [];
  const dailyUsage = usageHistory?.daily || [];

  const tokenChartOption = tokenRate && minutes.length > 0
    ? {
        tooltip: { ...glassChartTooltip(chartTheme.baseTooltip), trigger: "axis" as const },
        grid: chartTheme.baseGrid,
        xAxis: {
          type: "category" as const,
          data: minutes.map((m) => {
            const d = new Date(m.minute);
            return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
          }),
          ...glassAxis(chartTheme.baseAxis),
        },
        yAxis: [
          { type: "value" as const, name: t("monitoring.axis_total", "Total"), ...glassAxis(chartTheme.baseAxis) },
          { type: "value" as const, name: t("monitoring.axis_prompt_completion", "Prompt / Completion"), ...glassAxis(chartTheme.baseAxis) },
        ],
        series: [
          {
            name: t("monitoring.series_total_tokens", "Total Tokens"),
            type: "bar" as const,
            data: minutes.map((m) => m.total_tokens),
            itemStyle: { color: alpha(glass.accent, 0.7), borderRadius: [4, 4, 0, 0] },
          },
          {
            name: t("monitoring.series_prompt", "Prompt"),
            type: "line" as const,
            yAxisIndex: 1,
            data: minutes.map((m) => m.prompt_tokens),
            smooth: true,
            lineStyle: { color: glass.accent, width: 1.5 },
            itemStyle: { color: glass.accent },
          },
          {
            name: t("monitoring.series_completion", "Completion"),
            type: "line" as const,
            yAxisIndex: 1,
            data: minutes.map((m) => m.completion_tokens),
            smooth: true,
            lineStyle: { color: theme.palette.warning.main, width: 1.5 },
            itemStyle: { color: theme.palette.warning.main },
          },
        ],
      }
    : null;

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              {t("admin.auto_refresh", "Auto-refresh (10s)")}
            </Typography>
          }
        />
        <Tooltip title={t("monitoring.refresh_now", "Refresh now")}>
          <IconButton size="small" onClick={refresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Concurrency Gauges */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, mb: 1.5 }}>
        {t("admin.concurrency_slots", "LLM Concurrency Slots")}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Box sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2, background: glass.panel, border: `1px solid ${glass.panelBorder}`, backdropFilter: "blur(8px)" }}>
                <Skeleton height={80} />
              </Box>
            </Grid>
          ))
        ) : concurrency ? (
          <>
            <Grid item xs={12} sm={4}>
              <SlotGauge
                label={t("monitoring.channel_interactive", "Interactive")}
                active={concurrency.channels.interactive.active}
                max={concurrency.channels.interactive.reserved}
                color={glass.accent}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SlotGauge
                label={t("monitoring.channel_bulk", "Bulk")}
                active={concurrency.channels.bulk.active}
                max={concurrency.channels.bulk.max_theoretical}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SlotGauge
                label={t("monitoring.channel_voice", "Voice")}
                active={concurrency.channels.voice.active}
                max={concurrency.channels.voice.max}
                color={theme.palette.success.light}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Typography sx={{ color: textSecondary }}>
              {t("admin.no_redis", "Concurrency data unavailable (Redis not connected)")}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Token rate chart */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary }}>
            {t("admin.token_usage_rate", "Token Usage Rate (last 60 min)")}
          </Typography>
          {tokenRate && (
            <Chip
              size="small"
              label={`${(tokenRate.current_rate_per_min ?? 0).toLocaleString()} ${t("monitoring.tokens_per_min", "tokens/min")} · ${(tokenRate.total_last_hour ?? 0).toLocaleString()} ${t("monitoring.last_hour", "last hour")}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        {loading ? (
          <Skeleton variant="rectangular" height={chartTheme.chartHeight} />
        ) : tokenChartOption ? (
          <ReactECharts option={tokenChartOption} style={{ height: chartTheme.chartHeight }} />
        ) : (
          <Typography sx={{ color: textSecondary, py: 4, textAlign: "center" }}>
            {t("monitoring.no_token_data", "No token rate data available")}
          </Typography>
        )}
      </Box>

      {/* Usage History — Cumulative Stacked Area (#17) */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, mb: 1 }}>
          {t("admin.usage_history", "Token Usage History (30d, by channel)")}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={chartTheme.chartHeight} />
        ) : dailyUsage.length > 0 ? (
            <ReactECharts
              style={{ height: chartTheme.chartHeight + 40 }}
              option={{
                tooltip: {
                  ...glassChartTooltip(chartTheme.baseTooltip),
                  trigger: "axis" as const,
                },
                legend: {
                  data: [t("monitoring.channel_interactive", "Interactive"), t("monitoring.channel_bulk", "Bulk"), t("monitoring.channel_voice", "Voice")],
                  textStyle: { color: glass.textSecondary },
                  top: 0,
                },
                grid: { ...chartTheme.baseGrid, top: "14%" },
                xAxis: {
                  type: "category" as const,
                  data: dailyUsage.map((d) => d.date.slice(5)),
                  ...glassAxis(chartTheme.baseAxis),
                },
                yAxis: {
                  type: "value" as const,
                  name: t("monitoring.axis_tokens_cumulative", "Tokens (cumulative)"),
                  ...glassAxis(chartTheme.baseAxis),
                },
                series: [
                  {
                    name: t("monitoring.channel_interactive", "Interactive"),
                    type: "line" as const,
                    stack: "usage",
                    areaStyle: { color: alpha(glass.accent, 0.3) },
                    lineStyle: { color: glass.accent, width: 1.5 },
                    itemStyle: { color: glass.accent },
                    data: dailyUsage.map((d) => d.cum_interactive),
                    smooth: true,
                    emphasis: { focus: "series" as const },
                  },
                  {
                    name: t("monitoring.channel_bulk", "Bulk"),
                    type: "line" as const,
                    stack: "usage",
                    areaStyle: { color: alpha(theme.palette.secondary.main, 0.3) },
                    lineStyle: { color: theme.palette.secondary.main, width: 1.5 },
                    itemStyle: { color: theme.palette.secondary.main },
                    data: dailyUsage.map((d) => d.cum_bulk),
                    smooth: true,
                    emphasis: { focus: "series" as const },
                  },
                  {
                    name: t("monitoring.channel_voice", "Voice"),
                    type: "line" as const,
                    stack: "usage",
                    areaStyle: { color: alpha(theme.palette.success.light, 0.3) },
                    lineStyle: { color: theme.palette.success.light, width: 1.5 },
                    itemStyle: { color: theme.palette.success.light },
                    data: dailyUsage.map((d) => d.cum_voice),
                    smooth: true,
                    emphasis: { focus: "series" as const },
                  },
                ],
              }}
            />
          ) : (
            <Typography sx={{ color: textSecondary, py: 4, textAlign: "center" }}>
              {t("monitoring.no_usage_data", "No usage data available")}
            </Typography>
          )}
      </Box>

      {/* Cost summary cards */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, mb: 1.5 }}>
        {t("admin.cost_summary", "Cost Summary")}
      </Typography>
      <Grid container spacing={2}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Box sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2, background: glass.panel, border: `1px solid ${glass.panelBorder}`, backdropFilter: "blur(8px)" }}>
                <Skeleton height={60} />
              </Box>
            </Grid>
          ))
        ) : cost ? (
          [
            { label: t("monitoring.prompt_tokens", "Prompt Tokens"), value: (cost.tokens.prompt ?? 0).toLocaleString(), sub: `$${(cost.cost_usd.input ?? 0).toFixed(4)}`, color: glass.accent },
            { label: t("monitoring.completion_tokens", "Completion Tokens"), value: (cost.tokens.completion ?? 0).toLocaleString(), sub: `$${(cost.cost_usd.output ?? 0).toFixed(4)}`, color: theme.palette.secondary.main },
            { label: t("monitoring.total_mtd", "Total (MTD)"), value: (cost.tokens.total ?? 0).toLocaleString(), sub: `$${(cost.cost_usd.total ?? 0).toFixed(4)}`, color: theme.palette.warning.main },
          ].map((card, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  background: glass.panel,
                  border: `1px solid ${glass.panelBorder}`,
                  backdropFilter: "blur(8px)",
                  transition: "border-color 0.2s ease",
                  "&:hover": { borderColor: glass.headerDivider },
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" sx={{ color: textSecondary }}>{card.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" sx={{ color: textSecondary }}>
                  {card.sub}
                </Typography>
              </Box>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography sx={{ color: textSecondary }}>{t("monitoring.no_cost_data", "Cost data unavailable")}</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MonitoringDashboard;
